import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon, Mark } from './Icon';
import { useIsMock } from './mockFlag';

interface Props {
  title: string;
  /** External link of the title (the reel's source); opens in a new tab. */
  titleHref?: string | undefined;
  /** Path of the back button. Without it the brand mark shows. */
  back?: string;
  right?: ReactNode;
  /** Content of the sticky bottom bar (one primary action). */
  bar?: ReactNode;
  barHint?: string | undefined;
  /** Desktop width: "wide" for lists and side-by-side work views, default a readable form column. */
  width?: 'narrow' | 'wide';
  children: ReactNode;
}

/** Page frame: sticky header, content column, sticky bottom action bar with safe-area insets. */
export function Screen({ title, titleHref, back, right, bar, barHint, width = 'narrow', children }: Props) {
  const mock = useIsMock();
  return (
    <>
      <div className={width === 'wide' ? 'app app-wide' : 'app'}>
        <header className="top">
          {back ? (
            <Link className="iconbtn" to={back} aria-label="Back">
              <Icon name="left" />
            </Link>
          ) : (
            <Mark />
          )}
          <h1>
            {titleHref ? (
              <a
                className="title-link"
                href={titleHref}
                target="_blank"
                rel="noopener noreferrer"
                title="Open the source"
              >
                {title}
              </a>
            ) : (
              title
            )}
          </h1>
          {mock && (
            <span className="example" title="Mock mode: all data is example data">
              Example data
            </span>
          )}
          {right}
        </header>
        <main id="main">{children}</main>
      </div>
      {bar && (
        <div className={width === 'wide' ? 'bar bar-wide' : 'bar'}>
          <div className="wrap">
            {barHint && <p className="hint">{barHint}</p>}
            <div className="in">{bar}</div>
          </div>
        </div>
      )}
    </>
  );
}

export function HistoryButton({ reelId }: { reelId: string }) {
  return (
    <Link className="iconbtn" to={`/reels/${reelId}/history`} aria-label="History">
      <Icon name="history" />
    </Link>
  );
}

export function Loading({ title = 'Reel Machine' }: { title?: string }) {
  return (
    <Screen title={title}>
      <p className="muted" role="status">
        Loading…
      </p>
    </Screen>
  );
}

export function ErrorView({ title = 'Reel Machine', error, back }: { title?: string; error: unknown; back?: string }) {
  const message = error instanceof Error ? error.message : 'Something went wrong.';
  return (
    <Screen title={title} {...(back ? { back } : {})}>
      <div className="card warnc" role="alert">
        <h3>Something went wrong</h3>
        <p>{message}</p>
      </div>
    </Screen>
  );
}
